import { Scope } from 'wdc-cube';
import { MEASUREMENT_SOURCES, STATUS_TONES, STATUS_LABELS, formatMeasurement, measurementLabelOf, measurementTypeOf, METRIC_TYPE_IDS, systolicOf } from '../../../../domain';
import { reuseRows } from '../../../../components/rows';
import { TableModel } from '../../../../components/table';
import { CellScope, CollaboratorRowScope, ColumnScope } from './list/collaborator-list.scope';
/**
 * A tabela de colaboradores, montada fora de qualquer presenter.
 *
 * Duas telas mostram a mesma tabela: a lista de Colaboradores (admin, a base toda
 * ou a empresa) e Monitoramentos Compartilhados (o profissional, so os pacientes
 * que compartilharam com ele). As colunas, o formato de cada valor e o criterio de
 * ordenacao sao os mesmos - o que muda e QUEM pode ver o que, e isso e decisao de
 * cada presenter, nao da tabela.
 *
 * Funcoes soltas e nao uma classe base: heranca entre presenters amarraria os dois
 * ciclos de vida por causa de codigo de formatacao.
 */
/** As colunas de medicao, na ordem em que aparecem - a mesma do painel antigo. */
/**
 * As colunas de medicao, na ordem em que aparecem.
 *
 * A 205 (frequencia cardiaca de atividade) entrou por ultimo, em 13/09/2026: sem
 * ela, os instantes que so tem esse tipo - um por segundo de treino - viravam
 * linhas inteiras de tracos no historico, 11% das linhas de um dos historicos de
 * QA. Entrou nos DOIS paineis.
 */
export const MEASUREMENT_COLUMNS = [103, 155, 153, 154, 102, 204, 201, 202, 203, 205];
/** As colunas fixas que vem antes das medicoes. */
/**
 * A ordem em que a lista abre: mais criticos primeiro.
 *
 * E o `defaultOrderBy` do painel antigo (`tablePatients.tsx`), e nao um detalhe
 * estetico - a tela existe para o profissional ver quem esta pior. Fica FORA do
 * endereco: `sort` ausente significa esta ordem, entao o link limpo continua limpo
 * e so uma escolha explicita do leitor aparece na URL.
 */
export const DEFAULT_SORT = '-criticos';
export const FIXED_COLUMNS = [
    // O CAMPO continua `criticos`: e nome de ordenacao, entra na lista branca do
    // `safeSort` e viaja na URL. So o rotulo mudou - ver `STATUS_LABELS`.
    { field: 'criticos', label: 'Atenção' },
    { field: 'nome', label: 'Nome do colaborador' },
    // O CPF saiu da tabela a pedido do cliente (23/09/2026, B-31). O FILTRO por CPF
    // continua: buscar por ele e diferente de exibi-lo em toda linha. Um link antigo
    // com `sort=cpf` cai na ordem padrao, porque o campo saiu da lista branca.
    { field: 'origem', label: 'Dispositivo de Origem' },
    { field: 'atualizado', label: 'Última atualização' }
];
/**
 * Os nomes de coluna que esta tabela sabe ordenar - a lista branca do `sort` que
 * chega pela URL.
 *
 * Parametro e entrada de fora: `sort=qualquercoisa` num link nao pode virar uma
 * ordenacao por campo inexistente. Fica ao lado das colunas para nao envelhecer
 * separado delas.
 */
export const SORT_FIELDS = [
    ...FIXED_COLUMNS.map((column) => column.field),
    ...MEASUREMENT_COLUMNS.map((typeId) => `m${typeId}`)
];
/**
 * O modelo da tabela de colaboradores, com o universo de colunas ja declarado: as
 * fixas e as dez medicoes, cada uma com o valor pelo qual ordena. As duas presas sao
 * "Atenção" e o nome, que se leem como um bloco so (B-31).
 */
export function collaboratorTableModel(name, account, legacy) {
    const model = new TableModel({ name, account, pinStart: 2, legacy });
    model.setColumns(SORT_FIELDS.map((field) => ({ field, sortValue: (patient) => collaboratorSortValue(patient, field) })));
    return model;
}
export function buildCollaboratorRow(patient, update, onOpen, href, reuse, sleepLink) {
    // A linha que ja esta na tela, quando ha - ver `reuseRows`.
    const scope = reuse ?? new CollaboratorRowScope();
    scope.identity = patient.patientId;
    scope.patientId = patient.patientId;
    scope.href = href;
    scope.name = patient.patientName || '-';
    scope.company = patient.companyName ?? undefined;
    scope.criticalCount = patient.criticalMeasurementsCount ?? 0;
    const source = mostUsedSource(patient);
    scope.sourceTitle = source?.title;
    scope.sourceIcon = source?.icon;
    const latest = latestDate(patient);
    scope.updatedAt = latest ? formatDateTime(latest) : '-';
    scope.cells = MEASUREMENT_COLUMNS.map((typeId) => buildCell(patient, typeId, update, reuse?.cells.find((cell) => cell.identity === typeId), typeId === METRIC_TYPE_IDS.SONO ? sleepLink : undefined));
    scope.onOpen = onOpen;
    scope.update = update;
    return scope;
}
function buildCell(patient, typeId, update, reuse, link) {
    const scope = reuse ?? new CellScope();
    scope.identity = typeId;
    scope.update = update;
    // Reaproveitada, a celula volta ao vazio antes: o que vem abaixo so preenche
    // "quando ha", e sem isto ficaria o valor da carga anterior.
    scope.value = '-';
    scope.statusName = undefined;
    scope.statusColor = undefined;
    scope.href = link?.href ?? '';
    scope.onOpen = link?.open ?? Scope.ASYNC_ACTION;
    const measurement = patient.lastMeasurements?.find((item) => item.measurementTypeId === typeId);
    if (!measurement) {
        return scope;
    }
    scope.value = formatMeasurement(typeId, measurement.value);
    if (measurementTypeOf(typeId)?.hasStatus) {
        const level = measurement.measurementStatusLevel;
        scope.statusName = STATUS_LABELS[level];
        scope.statusColor = STATUS_TONES[level];
    }
    return scope;
}
/** Os campos de medicao, na ordem padrao. */
export const MEASUREMENT_FIELDS = MEASUREMENT_COLUMNS.map((typeId) => `m${typeId}`);
export function buildCollaboratorColumns(sort, update, onSort, layout, 
/** As colunas que ja estao na tela: a mesma coluna e o mesmo scope, atualizado. */
reuse) {
    /*
     * O nome vem do `MEASUREMENT_NAMES`, como em toda tela de medicao.
     *
     * Esta tabela tinha vocabulario proprio ("Batimentos Cardíacos" onde o painel
     * dizia "Frequência Cardíaca", "Pressão" onde ele dizia "Pressão Arterial"),
     * copiado do `converterParaListaPacientes` da referencia. Um nome so - B-03.
     */
    const measurement = (field) => ({ field, label: measurementLabelOf(Number(field.slice(1))) });
    const focus = layout?.focus;
    const others = (layout?.measurements ?? MEASUREMENT_FIELDS).filter((field) => field !== focus);
    const [atencao, nome, ...restoFixo] = FIXED_COLUMNS;
    const ordered = [atencao, nome, ...(focus ? [measurement(focus)] : []), ...restoFixo, ...others.map(measurement)];
    const current = sort ?? DEFAULT_SORT;
    const descending = current.startsWith('-');
    const field = descending ? current.slice(1) : current;
    return reuseRows(reuse, ordered, (column) => column.field, (column, reused) => buildColumn(column, reused));
    function buildColumn(column, reused) {
        const scope = reused ?? new ColumnScope();
        scope.identity = column.field;
        scope.field = column.field;
        scope.label = column.label;
        scope.sorted = column.field === field;
        scope.descending = scope.sorted && descending;
        scope.onSort = () => onSort(column.field);
        // So as medicoes se arrastam: as fixas sao o que identifica a linha. Atribuido
        // nos dois casos: o scope reaproveitado pode ter sido movel na montagem anterior.
        const onDrop = layout?.onDrop;
        scope.movable = !!onDrop && column.field.startsWith('m') && column.field !== focus;
        scope.onDrop = scope.movable ? (source) => onDrop(source, column.field) : () => undefined;
        scope.update = update;
        return scope;
    }
}
/**
 * O valor pelo qual uma linha se ordena, por campo.
 *
 * Medicao ordena pelo valor BRUTO, nao pelo texto: `'9 bpm'` vem depois de
 * `'85 bpm'` em ordem alfabetica. A pressao e a excecao - ordena pela sistolica,
 * porque `120074` comparado cru daria uma ordem que nao significa nada.
 */
export function collaboratorSortValue(patient, field) {
    switch (field) {
        case 'criticos':
            return patient.criticalMeasurementsCount ?? 0;
        case 'nome':
            return patient.patientName ?? null;
        case 'origem':
            return mostUsedSource(patient)?.title ?? null;
        case 'atualizado':
            return latestDate(patient) ?? null;
        default:
            break;
    }
    if (field.startsWith('m')) {
        const typeId = Number(field.slice(1));
        const measurement = patient.lastMeasurements?.find((item) => item.measurementTypeId === typeId);
        if (!measurement) {
            return null;
        }
        return typeId === 102 ? systolicOf(measurement.value) : measurement.value;
    }
    return null;
}
function mostUsedSource(patient) {
    const measurements = patient.lastMeasurements ?? [];
    if (measurements.length === 0) {
        return undefined;
    }
    const counts = new Map();
    for (const measurement of measurements) {
        counts.set(measurement.measurementSourceId, (counts.get(measurement.measurementSourceId) ?? 0) + 1);
    }
    let best;
    for (const [id, count] of counts) {
        if (!best || count > best.count) {
            best = { id, count };
        }
    }
    return MEASUREMENT_SOURCES.find((source) => source.id === best?.id);
}
/** A medicao mais recente do paciente, como string ISO comparavel. */
function latestDate(patient) {
    let latest;
    for (const measurement of patient.lastMeasurements ?? []) {
        if (measurement.date && (!latest || measurement.date > latest)) {
            latest = measurement.date;
        }
    }
    return latest;
}
/**
 * `2026-09-11T14:32:00` em `11/09/2026 - 14:32`.
 *
 * O separador e " - ", como o `format(..., 'dd/MM/yyyy - HH:mm')` da referencia.
 *
 * Por fatia de string, nao por `new Date()`: o backend manda sem fuso, e o `Date`
 * leria como local para depois o formatador devolver outro dia.
 */
function formatDateTime(iso) {
    const [date, time] = iso.split('T');
    const [year, month, day] = date.split('-');
    return time ? `${day}/${month}/${year} - ${time.slice(0, 5)}` : `${day}/${month}/${year}`;
}
//# sourceMappingURL=collaborator-table.js.map